/* ─────────────────────────────────────────────────────────────────────────────
   presence.ts — what somebody wants other people to see.

   **Owned by website-main.** Copied into every Worker that renders a person.
   Change it here and run `node scripts/sync-shared.mjs` from the workspace root;
   `shared/copies.test.ts` in each copy catches an edit in place, and `--check`
   is the only thing that catches the owner moving and a copy being missed.

   ── The vocabulary is Relay's, and that is not a coincidence ────────────────

   Relay has had presence since before this file existed
   (`relay/src/data/types.h`). Presence appearing in two products with two sets
   of words would be two features that look like one, and the person switching
   between them would be the one who found out.

   So the six states here are Relay's six, in Relay's order, and the colours are
   `presenceColor()` in `relay/src/ui/widgets.cpp`.

   ── The order is load-bearing ───────────────────────────────────────────────

   Relay's own comment, on why `DoNotDisturb` sits after `Offline` rather than
   next to `Busy` where it belongs:

       "appended after Offline because the integer rides the presence wire"

   Its enum is transmitted as a number. Reordering this array to read better
   would silently make one product's "busy" another's "offline", so
   `RELAY_ORDER` is the wire and the array below is it. This file's own wire is
   strings, because it is JSON over a socket and a name that survives a
   reordering is worth more here than two bytes.

   ── What is stored, and what is worked out ──────────────────────────────────

   Only an override is stored (`users.presence`). Everything else is derived
   from when somebody was last seen, because a heartbeat is not worth a database
   write and because "available" is an observation rather than a decision.
   ───────────────────────────────────────────────────────────────────────────── */

export const PRESENCE_STATES = ['available', 'away', 'busy', 'dnd', 'offline', 'unknown'] as const;

export type Presence = (typeof PRESENCE_STATES)[number];

/**
 * Relay's enum order, which is Relay's wire.
 *
 * `relay::Presence` is `Available, Away, Busy, Offline, DoNotDisturb, Unknown`
 * and its integer value is transmitted. **Do not reorder this to match
 * `PRESENCE_STATES`** — they are deliberately different, and this one is the one
 * that has to agree with a C++ enum in another repository.
 */
export const RELAY_ORDER: readonly Presence[] = [
  'available',
  'away',
  'busy',
  'offline',
  'dnd',
  'unknown'
] as const;

/** A number off Relay's wire. Anything out of range is `unknown`, which is the
 *  state that draws no dot — the honest answer to "we do not know". */
export const presenceFromRelay = (value: number): Presence =>
  RELAY_ORDER[value] ?? 'unknown';

/**
 * The number Relay's wire expects.
 *
 * **Never send `unknown`.** Relay's receiving client validates the integer as
 * `p >= Available && p <= DoNotDisturb` and drops anything outside that, so a 5
 * on the wire is a frame that silently does nothing. `unknown` is a local state
 * meaning "we have not heard", and there is nothing to transmit about it.
 */
export const presenceToRelay = (presence: Presence): number => {
  const index = RELAY_ORDER.indexOf(presence);
  return index < 0 ? RELAY_ORDER.indexOf('unknown') : index;
};

/** Is this a state a person may choose for themselves? */
export const isSettablePresence = (value: unknown): value is Presence =>
  value === 'available' || value === 'away' || value === 'busy' || value === 'dnd';

/** Is this any known state? */
export const isPresence = (value: unknown): value is Presence =>
  typeof value === 'string' && (PRESENCE_STATES as readonly string[]).includes(value);

/**
 * The design-system colour token each state uses.
 *
 * The same mapping as `presenceColor()` in Relay's `widgets.cpp`: teal for
 * available, amber for away, danger for both busy and do-not-disturb, muted for
 * offline. `unknown` has none — Relay draws no dot at all, and a grey dot for
 * "we do not know" reads as "offline", which is a different claim.
 */
export const PRESENCE_COLOUR: Record<Presence, string | null> = {
  // `--signal`, not `--signal-500`. The teal ramp is `--teal-500` and the
  // semantic name has no numeral — a `--signal-500` that exists in no token
  // file paints nothing at all, silently, and available is the state this
  // feature is mostly for. Checked against `_ds/mauvely/tokens/colors.css`.
  available: 'var(--signal)',
  away: 'var(--warning-500)',
  busy: 'var(--danger-500)',
  // The same colour as busy, deliberately. Relay distinguishes them with a
  // no-entry bar drawn over the dot rather than with a sixth colour, because a
  // person cannot learn six.
  dnd: 'var(--danger-500)',
  offline: 'var(--text-quiet)',
  unknown: null
};

/** What a person is told each state means. Short, because it is a tooltip. */
export const PRESENCE_LABEL: Record<Presence, string> = {
  available: 'Available',
  away: 'Away',
  busy: 'Busy',
  dnd: 'Do not disturb',
  offline: 'Offline',
  unknown: ''
};

/**
 * The dot, as two style objects.
 *
 * ── Why this is here and not a component ────────────────────────────────────
 *
 * `shared/` has only ever carried logic, and `scripts/sync-shared.mjs` has
 * never copied a `.tsx`. Five apps draw this dot on five differently-shaped
 * chips — three sizes, three background colours, one of them a bare `<span>`
 * with no wrapper at all — so a shared component would arrive with five props
 * describing where it was and still not fit.
 *
 * What must not differ between apps is the part that carries meaning: which
 * colour, and whether do-not-disturb gets its bar. That is this function. The
 * JSX around it is two elements and is each app's own.
 *
 * `ring` is the colour of the cut-out, which is the chip's background rather
 * than the page's — website-main draws its chip on violet in one nav and on
 * near-white in another.
 *
 * Returns `null` for `unknown`: Relay draws no dot at all when it has not
 * heard, and a grey one would say "offline", which is a different claim.
 */
export function presenceDot(
  presence: Presence,
  size: number,
  ring: string
): { dot: Record<string, string | number>; bar: Record<string, string | number> | null } | null {
  const colour = PRESENCE_COLOUR[presence];
  if (!colour) return null;

  const dot = {
    position: 'absolute',
    right: -1,
    bottom: -1,
    width: size,
    height: size,
    borderRadius: 999,
    background: colour,
    boxShadow: `0 0 0 ${Math.max(1.5, size / 6)}px ${ring}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none'
  } as const;

  // Busy and do-not-disturb share a colour, so the bar is the only thing that
  // tells them apart — the same choice Relay makes, and the reason there is no
  // sixth colour.
  const bar =
    presence === 'dnd'
      ? ({
          width: Math.max(4, size * 0.56),
          height: Math.max(1, Math.round(size / 7)),
          borderRadius: 999,
          background: ring
        } as const)
      : null;

  return { dot: { ...dot }, bar: bar ? { ...bar } : null };
}

/**
 * Quiet for this long and somebody is away.
 *
 * Five minutes: long enough that reading a document does not make you look
 * gone, short enough that leaving your desk is visible before the person
 * messaging you has given up.
 */
export const AWAY_AFTER_MS = 5 * 60 * 1000;

/**
 * Quiet for this long and somebody is offline.
 *
 * A socket that closes cleanly reports offline immediately; this is the
 * backstop for the one that does not — a laptop lid, a train tunnel, a killed
 * process. Fifteen minutes rather than five, because a false "offline" is worse
 * than a stale "away": it tells somebody not to bother.
 */
export const OFFLINE_AFTER_MS = 15 * 60 * 1000;

/*
 * ── These windows are not Relay's, and that is deliberate ───────────────────
 *
 * Relay ages a peer to offline after 150 seconds with no beat
 * (`kPresenceTtlMs`), which is right for what it is asking: is this person's
 * app alive, in this conversation, now.
 *
 * This asks a different question — is this person at their desk today — across
 * a browser tab that may be backgrounded, a laptop that sleeps and a network
 * that drops. Two and a half minutes there would show half of everybody as
 * offline all afternoon.
 *
 * Somebody will eventually notice the two numbers differ and want to reconcile
 * them. They should not.
 */

export interface PresenceInput {
  /** What the person chose, or null. Survives closing the laptop. */
  override: Presence | null;
  /** When they were last heard from. Null means never. */
  lastSeenAt: number | null;
  /** Sockets open right now. Zero is not offline on its own — see below. */
  connections: number;
  now: number;
}

/**
 * What a client is handed, over the socket and over `GET /api/presence` alike.
 *
 * It carries the **inputs** as well as the answer, which is the whole reason
 * `PresenceDO` needs no timer: a tab that holds the socket runs
 * `effectivePresence()` on its own clock, so somebody going quiet turns amber
 * five minutes later without waking anything. Handing over only `presence`
 * would have meant an alarm on the server or a poll in the browser.
 *
 * Spelled as `PresenceInput` minus `now` so the two cannot drift: a field added
 * to the derivation is a field the wire must carry, and this makes that a
 * compile error rather than a stale dot.
 */
export type PresenceState = Omit<PresenceInput, 'now'> & { presence: Presence };

/**
 * What to show.
 *
 * An override wins over everything, including being offline: somebody who set
 * "do not disturb" and closed the laptop meant it, and showing them as offline
 * would quietly answer a different question from the one they answered.
 *
 * The exception is `available`. Choosing "available" and then leaving is the one
 * override that decays, because it is a claim about the present rather than a
 * preference — and an "available" dot on somebody who left at five is the single
 * thing that makes a presence indicator not worth having.
 */
export function effectivePresence(input: PresenceInput): Presence {
  const { override, lastSeenAt, connections, now } = input;

  const quietFor = lastSeenAt === null ? Number.POSITIVE_INFINITY : now - lastSeenAt;
  const derived: Presence =
    connections > 0 && quietFor < AWAY_AFTER_MS
      ? 'available'
      : quietFor >= OFFLINE_AFTER_MS
        ? 'offline'
        : 'away';

  if (override === null) return derived;

  // `busy` and `dnd` are decisions and hold. `away` holds too — somebody who
  // says they are away is away whatever their socket says.
  if (override !== 'available') return override;

  // `available` decays into whatever is actually true.
  return derived;
}
